/**
 * i18n Check Module
 * Detects non-internationalized strings in code
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Patterns for hardcoded UI strings

// JSX text content (between tags)
const JSX_TEXT_PATTERN = />([A-Z][a-z]+(?:\s+[a-z]+)*[.!?]?)</gi;

// JSX attribute strings
const JSX_ATTR_PATTERNS = [
  { pattern: /placeholder\s*=\s*["']([^"']+)["']/gi, name: 'placeholder' },
  { pattern: /title\s*=\s*["']([A-Z][^"']+)["']/gi, name: 'title' },
  { pattern: /aria-label\s*=\s*["']([^"']+)["']/gi, name: 'aria-label' },
  { pattern: /alt\s*=\s*["']([A-Z][^"']+)["']/gi, name: 'alt' },
  { pattern: /label\s*=\s*["']([A-Z][^"']+)["']/gi, name: 'label' },
];

// C# error messages
const CSHARP_ERROR_PATTERNS = [
  { pattern: /throw\s+new\s+\w*Exception\s*\(\s*["']([^"']+)["']/gi, name: 'Exception message' },
  { pattern: /ModelState\.AddModelError\s*\([^,]+,\s*["']([^"']+)["']/gi, name: 'Validation error' },
  { pattern: /\.WithMessage\s*\(\s*["']([^"']+)["']/gi, name: 'FluentValidation message' },
  { pattern: /BadRequest\s*\(\s*["']([^"']+)["']/gi, name: 'BadRequest message' },
  { pattern: /NotFound\s*\(\s*["']([^"']+)["']/gi, name: 'NotFound message' },
];

// Notification patterns
const NOTIFICATION_PATTERNS = [
  { pattern: /toast\.\w+\s*\(\s*["']([^"']+)["']/gi, name: 'Toast notification' },
  { pattern: /notify\s*\(\s*["']([^"']+)["']/gi, name: 'Notification' },
  { pattern: /alert\s*\(\s*["']([^"']+)["']/gi, name: 'Alert' },
  { pattern: /confirm\s*\(\s*["']([^"']+)["']/gi, name: 'Confirm dialog' },
];

// Exclusion patterns (strings that don't need translation)
const EXCLUDED_PATTERNS = [
  /^[a-z]+$/i, // Single words (likely identifiers)
  /^[A-Z_]+$/, // CONSTANTS
  /^\d+$/, // Numbers
  /^[a-z0-9-_]+$/i, // IDs, slugs
  /^https?:\/\//i, // URLs
  /^[a-zA-Z0-9._%+-]+@/, // Emails
  /^\{.*\}$/, // Template variables
  /^#[0-9a-f]+$/i, // Color codes
];

export async function checkI18n(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    // Skip non-UI files
    if (!isUiFile(file.relativePath)) continue;

    const lines = file.content.split('\n');

    // React/TSX checks
    if (['tsx', 'jsx'].includes(file.language)) {
      // Check for hardcoded JSX text
      let match;
      JSX_TEXT_PATTERN.lastIndex = 0;
      while ((match = JSX_TEXT_PATTERN.exec(file.content)) !== null) {
        const text = match[1].trim();

        if (!shouldExclude(text) && text.length > 2) {
          const lineNumber = getLineNumber(file.content, match.index);

          findings.push({
            id: generateFindingId('i18n'),
            category: 'i18n',
            severity: 'warning',
            title: 'Hardcoded UI Text',
            description: `Text "${truncateCode(text, 30)}" should use translation function.`,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
            suggestion: `Use {t('key')} instead of hardcoded text. Example: {t('buttons.submit')}`,
            autoFixable: false,
          });
        }
      }

      // Check JSX attributes
      for (const { pattern, name } of JSX_ATTR_PATTERNS) {
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const text = match[1].trim();

          if (!shouldExclude(text) && text.length > 2) {
            const lineNumber = getLineNumber(file.content, match.index);

            findings.push({
              id: generateFindingId('i18n'),
              category: 'i18n',
              severity: 'warning',
              title: `Non-translated ${name}`,
              description: `The ${name} attribute "${truncateCode(text, 30)}" should be translated.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
              suggestion: `Use {t('key')} for the ${name} attribute.`,
              autoFixable: false,
            });
          }
        }
      }

      // Check notification messages
      for (const { pattern, name } of NOTIFICATION_PATTERNS) {
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const text = match[1].trim();

          if (!shouldExclude(text) && text.length > 2) {
            const lineNumber = getLineNumber(file.content, match.index);

            findings.push({
              id: generateFindingId('i18n'),
              category: 'i18n',
              severity: 'warning',
              title: `Non-translated ${name}`,
              description: `${name} message "${truncateCode(text, 30)}" should be translated.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
              suggestion: `Use t('key') for notification messages.`,
              autoFixable: false,
            });
          }
        }
      }

      // Check for missing translation function import
      if (!file.content.includes('useTranslation') && !file.content.includes('from \'react-i18next\'')) {
        const hasHardcodedStrings = JSX_TEXT_PATTERN.test(file.content);
        if (hasHardcodedStrings) {
          findings.push({
            id: generateFindingId('i18n'),
            category: 'i18n',
            severity: 'info',
            title: 'Missing i18n Setup',
            description: 'This file has UI text but does not import useTranslation hook.',
            file: file.relativePath,
            line: 1,
            suggestion: "Add: import { useTranslation } from 'react-i18next';",
            autoFixable: true,
          });
        }
      }
    }

    // C# checks
    if (file.language === 'csharp') {
      // Check error messages
      for (const { pattern, name } of CSHARP_ERROR_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const text = match[1].trim();

          // Error messages in controllers/services should be translated
          if (text.length > 10 && !shouldExclude(text)) {
            const lineNumber = getLineNumber(file.content, match.index);

            findings.push({
              id: generateFindingId('i18n'),
              category: 'i18n',
              severity: 'warning',
              title: `Non-translated ${name}`,
              description: `${name} "${truncateCode(text, 30)}" should use localization.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
              suggestion: 'Use IStringLocalizer or resource files for error messages.',
              autoFixable: false,
            });
          }
        }
      }
    }
  }

  return findings;
}

function isUiFile(filePath: string): boolean {
  const lower = filePath.toLowerCase();
  return (
    lower.includes('/pages/') ||
    lower.includes('/components/') ||
    lower.includes('/views/') ||
    lower.includes('/ui/') ||
    lower.endsWith('.tsx') ||
    lower.endsWith('.jsx') ||
    (lower.includes('controller') && lower.endsWith('.cs'))
  );
}

function shouldExclude(text: string): boolean {
  // Check if text matches any exclusion pattern
  for (const pattern of EXCLUDED_PATTERNS) {
    if (pattern.test(text)) return true;
  }

  // Skip very short texts
  if (text.length <= 2) return true;

  // Skip if it looks like code
  if (text.includes('(') || text.includes(')') || text.includes('{')) return true;

  // Skip if mostly numbers/symbols
  const alphaChars = text.replace(/[^a-zA-Z]/g, '').length;
  if (alphaChars < text.length * 0.5) return true;

  return false;
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}
